import React, { useState, useEffect } from "react";
import { Check, X, MessageSquare, Calendar, Target, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchObjectivesByTeacher,
  acceptObjective,
  rejectObjective,
  clearError,
  resetObjectives,
} from "../../../redux/annualPersonalObjectiveSlice";
import PerformanceReview from "./PerformanceReview";
import { fetchPerformanceReviewsByTeacher, resetReviews } from "../../../redux/performanceReviewSlice";

const MyObjectives = ({ teacherEmail, isModalOpen = true }) => {
  const dispatch = useDispatch();
  const { objectives, loading, error, count } = useSelector(
    (state) => state.annualPersonalObjective
  );

  const [notes, setNotes] = useState({});
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [isYearObjectivesModalOpen, setIsYearObjectivesModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);

  // Fetch objectives and performance reviews when component mounts, teacherEmail changes, or modal opens
  useEffect(() => {
    if (teacherEmail && isModalOpen) {
      // Reset objectives first to avoid showing stale data
      dispatch(resetObjectives());
      dispatch(fetchObjectivesByTeacher(teacherEmail));
      // Also fetch performance reviews
      dispatch(resetReviews());
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail, isModalOpen]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleAcceptObjective = async (id) => {
    try {
      const objective = objectives.find((obj) => obj.id === id);
      await dispatch(
        acceptObjective({
          objectiveId: id,
          teacherNotes: notes[id] || objective?.teacherNotes || "",
        })
      ).unwrap();
      toast.success("Objective accepted successfully");
      // Refresh objectives
      dispatch(fetchObjectivesByTeacher(teacherEmail));
      // Refresh modal if open
      if (isYearObjectivesModalOpen && selectedYear) {
        setTimeout(() => {
          setIsYearObjectivesModalOpen(false);
          setIsYearObjectivesModalOpen(true);
        }, 100);
      }
    } catch (err) {
      toast.error(err || "Failed to accept objective");
    }
  };

  const handleRejectObjective = async (id) => {
    const objective = objectives.find((obj) => obj.id === id);
    if (!notes[id] || !notes[id].trim()) {
      if (!objective?.teacherNotes) {
        toast.error("Please add notes explaining why you're rejecting this objective");
        return;
      }
    }
    try {
      await dispatch(
        rejectObjective({
          objectiveId: id,
          teacherNotes: notes[id] || objective?.teacherNotes || "",
        })
      ).unwrap();
      toast.success("Objective rejected. Admin can now edit it.");
      // Refresh objectives
      dispatch(fetchObjectivesByTeacher(teacherEmail));
      // Refresh modal if open
      if (isYearObjectivesModalOpen && selectedYear) {
        setTimeout(() => {
          setIsYearObjectivesModalOpen(false);
          setIsYearObjectivesModalOpen(true);
        }, 100);
      }
    } catch (err) {
      toast.error(err || "Failed to reject objective");
    }
  };

  const handleSaveNotes = (id) => {
    // Notes are saved when accepting/rejecting, but we can keep this for UI feedback
    setEditingNotesId(null);
    toast.success("Notes saved. Remember to accept or reject the objective.");
  };

  const pendingObjectives = (objectives || []).filter((obj) => obj.status === "pending");
  const acceptedObjectives = (objectives || []).filter((obj) => obj.status === "accepted" || obj.status === "completed");
  const rejectedObjectives = (objectives || []).filter((obj) => obj.status === "rejected");

  // Group objectives by year
  const objectivesByYear = (objectives || []).reduce((acc, obj) => {
    const year = obj.year || obj.performanceYear || "Unknown";
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push({
      ...obj,
      performanceYear: obj.year || obj.performanceYear,
      minimum: obj.minExpectation || obj.minimum,
      createdDate: obj.createdAt || obj.createdDate,
    });
    return acc;
  }, {});

  const years = Object.keys(objectivesByYear).sort((a, b) => b.localeCompare(a));

  const handleViewYearObjectives = (year) => {
    setSelectedYear(year);
    setIsYearObjectivesModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#241763] to-[#3d2a7a] rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Target size={28} className="text-yellow-300" />
          <h2 className="text-2xl font-bold">My Personal Objectives</h2>
        </div>
        <p className="text-gray-200 text-sm">
          Review and manage your performance objectives for each year
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800 mb-1">Pending Review</p>
              <p className="text-3xl font-bold text-yellow-700">
                {count?.pending || pendingObjectives.length}
              </p>
              <p className="text-xs text-yellow-600 mt-1">Awaiting your action</p>
            </div>
            <div className="bg-yellow-300 rounded-full p-4 shadow-inner">
              <Clock className="text-yellow-800" size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800 mb-1">Accepted</p>
              <p className="text-3xl font-bold text-green-700">
                {count?.accepted || acceptedObjectives.length}
              </p>
              <p className="text-xs text-green-600 mt-1">Locked for the year</p>
            </div>
            <div className="bg-green-300 rounded-full p-4 shadow-inner">
              <CheckCircle2 className="text-green-800" size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-800 mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-700">
                {count?.rejected || rejectedObjectives.length}
              </p>
              <p className="text-xs text-red-600 mt-1">Awaiting admin review</p>
            </div>
            <div className="bg-red-300 rounded-full p-4 shadow-inner">
              <AlertCircle className="text-red-800" size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Review Card */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#241763] text-white rounded-lg p-3">
              <Calendar size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Performance Review</h3>
              <p className="text-sm text-gray-500 mt-1">View your performance reviews</p>
            </div>
          </div>
          <button
            onClick={() => setShowPerformanceReview(!showPerformanceReview)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            {showPerformanceReview ? "Hide" : "View"}
          </button>
        </div>
      </div>

      {/* Performance Review Section */}
      {showPerformanceReview && (
        <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5">
          <PerformanceReview 
            teacherEmail={teacherEmail} 
            isAdmin={false} 
            isModalOpen={showPerformanceReview}
          />
        </div>
      )}

      {/* Objectives by Year List */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="text-[#241763]" size={24} />
          <h3 className="text-xl font-bold text-gray-800">Objectives by Year</h3>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <p>Loading objectives...</p>
          </div>
        ) : years.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {years.map((year) => {
              const yearObjectives = objectivesByYear[year];
              const pendingCount = yearObjectives.filter((o) => o.status === "pending").length;
              const acceptedCount = yearObjectives.filter((o) => o.status === "accepted" || o.status === "completed").length;
              const rejectedCount = yearObjectives.filter((o) => o.status === "rejected").length;
              
              return (
                <div
                  key={year}
                  className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#241763] hover:shadow-lg cursor-pointer transition-all duration-200 group"
                  onClick={() => handleViewYearObjectives(year)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-[#241763] text-white rounded-lg p-2 group-hover:bg-[#3d2a7a] transition-colors">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-xl text-[#241763]">Year {year}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {yearObjectives.length} objective{yearObjectives.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <span className="text-[#241763] group-hover:text-[#3d2a7a] font-semibold">View →</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {pendingCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <Clock size={14} />
                        {pendingCount} Pending
                      </span>
                    )}
                    {acceptedCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <CheckCircle2 size={14} />
                        {acceptedCount} Accepted
                      </span>
                    )}
                    {rejectedCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <AlertCircle size={14} />
                        {rejectedCount} Rejected
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
            <Target className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 font-medium text-lg mb-2">No objectives assigned yet</p>
            <p className="text-gray-500 text-sm">Your objectives will appear here once they are assigned by your admin.</p>
          </div>
        )}
      </div>

      {/* Year Objectives Modal */}
      {isYearObjectivesModalOpen && selectedYear && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#241763] to-[#3d2a7a] text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-2">
                  <Calendar size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Objectives for Year {selectedYear}</h2>
                  <p className="text-gray-200 text-sm mt-1">
                    {objectivesByYear[selectedYear]?.length || 0} objective{objectivesByYear[selectedYear]?.length !== 1 ? 's' : ''} assigned
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsYearObjectivesModalOpen(false);
                  setSelectedYear(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
              <div className="space-y-5">
                {objectivesByYear[selectedYear]?.map((objective, index) => {
                  const normalizedObjective = {
                    ...objective,
                    performanceYear: objective.year || objective.performanceYear,
                    minimum: objective.minExpectation || objective.minimum,
                    createdDate: objective.createdAt || objective.createdDate,
                  };
                  
                  return (
                    <div
                      key={objective.id}
                      className={`border-2 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow ${
                        objective.status === "accepted"
                          ? "border-green-400 bg-gradient-to-br from-green-50 to-green-100"
                          : objective.status === "rejected"
                          ? "border-red-400 bg-gradient-to-br from-red-50 to-red-100"
                          : "border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100"
                      }`}
                    >
                    {/* Objective Header */}
                    <div className="mb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`rounded-full p-2 ${
                            objective.status === "accepted"
                              ? "bg-green-200"
                              : objective.status === "rejected"
                              ? "bg-red-200"
                              : "bg-yellow-200"
                          }`}>
                            <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                                  objective.status === "accepted"
                                    ? "bg-green-200 text-green-800"
                                    : objective.status === "rejected"
                                    ? "bg-red-200 text-red-800"
                                    : "bg-yellow-200 text-yellow-800"
                                }`}
                              >
                                {objective.status === "accepted" && <CheckCircle2 size={14} />}
                                {objective.status === "rejected" && <AlertCircle size={14} />}
                                {objective.status === "pending" && <Clock size={14} />}
                                {objective.status === "accepted"
                                  ? "Accepted"
                                  : objective.status === "rejected"
                                  ? "Rejected"
                                  : "Pending Review"}
                              </span>
                              {normalizedObjective.createdDate && (
                                <span className="text-xs text-gray-600 flex items-center gap-1">
                                  <Calendar size={12} />
                                  {new Date(normalizedObjective.createdDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <h4 className="font-bold text-lg text-gray-800 leading-tight">
                              {objective.objective}
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Performance Levels */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                      <div className="bg-white border-2 border-blue-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <p className="text-sm font-bold text-blue-700">Minimum</p>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {normalizedObjective.minimum || "Not specified"}
                        </p>
                      </div>
                      <div className="bg-white border-2 border-green-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <p className="text-sm font-bold text-green-700">On Target</p>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {objective.onTarget || "Not specified"}
                        </p>
                      </div>
                      <div className="bg-white border-2 border-purple-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                          <p className="text-sm font-bold text-purple-700">Exceptional</p>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {objective.exceptional || "Not specified"}
                        </p>
                      </div>
                    </div>
                    {/* Notes Section */}
                    <div className="mb-5 bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="text-[#241763]" size={18} />
                        <label className="text-sm font-semibold text-gray-700">
                          Your Notes {objective.status === "pending" && <span className="text-gray-400 font-normal">(Optional)</span>}
                          {objective.status === "rejected" && <span className="text-red-600 font-normal">(Required for rejection)</span>}
                        </label>
                      </div>
                      {editingNotesId === objective.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={notes[objective.id] || objective.teacherNotes || ""}
                            onChange={(e) => {
                              setNotes({
                                ...notes,
                                [objective.id]: e.target.value,
                              });
                            }}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#241763] focus:border-transparent"
                            rows="4"
                            placeholder="Add your notes, comments, or feedback about this objective..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveNotes(objective.id)}
                              className="bg-[#241763] text-white px-5 py-2 rounded-lg hover:bg-[#1a1247] text-sm font-medium transition-colors flex items-center gap-2"
                            >
                              <MessageSquare size={16} />
                              Save Notes
                            </button>
                            <button
                              onClick={() => {
                                setEditingNotesId(null);
                                setNotes({ ...notes, [objective.id]: objective.teacherNotes || "" });
                              }}
                              className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className={`text-sm mb-3 p-3 rounded-lg ${
                            objective.teacherNotes 
                              ? "bg-blue-50 text-gray-700 border border-blue-200" 
                              : "bg-gray-50 text-gray-500 italic border border-gray-200"
                          }`}>
                            {objective.teacherNotes || "No notes added yet. Click 'Add Notes' to add your comments."}
                          </p>
                          <button
                            onClick={() => {
                              setEditingNotesId(objective.id);
                              setNotes({
                                ...notes,
                                [objective.id]: objective.teacherNotes || "",
                              });
                            }}
                            className="text-[#241763] hover:text-[#1a1247] text-sm font-medium flex items-center gap-2 transition-colors"
                          >
                            <MessageSquare size={16} />
                            {objective.teacherNotes ? "Edit Notes" : "Add Notes"}
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Action Buttons */}
                    {objective.status === "pending" && (
                      <div className="flex gap-3 pt-4 border-t border-gray-300">
                        <button
                          onClick={async () => {
                            await handleAcceptObjective(objective.id);
                          }}
                          className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-800 font-semibold shadow-md hover:shadow-lg transition-all"
                        >
                          <CheckCircle2 size={20} />
                          Accept Objective
                        </button>
                        <button
                          onClick={async () => {
                            if (!notes[objective.id] && !objective.teacherNotes) {
                              setEditingNotesId(objective.id);
                              setNotes({ ...notes, [objective.id]: "" });
                              toast.info("Please add notes explaining why you're rejecting this objective");
                            } else {
                              await handleRejectObjective(objective.id);
                            }
                          }}
                          className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-lg hover:from-red-700 hover:to-red-800 font-semibold shadow-md hover:shadow-lg transition-all"
                        >
                          <X size={20} />
                          Reject Objective
                        </button>
                      </div>
                    )}
                    {/* Show notes for accepted/rejected objectives */}
                    {objective.teacherNotes && objective.status !== "pending" && (
                      <div className="mt-4 p-4 bg-white rounded-lg border-2 border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <MessageSquare size={14} />
                          Your Notes:
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {objective.teacherNotes}
                        </p>
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MyObjectives;

